import { type FC, type PropsWithChildren, useEffect, useState } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { SignalContext } from './SignalContext';
import { useLogger, useToken } from '@synqo/frontend-component-library';
import { MESSAGES_API } from '@Constants/backendConstants';
import { useSession } from '@Contexts/session';

export const SignalProvider: FC<PropsWithChildren> = ({ children }) => {
    const { getToken } = useToken();
    const { error } = useLogger();
    const {
        current: { partnershipId },
    } = useSession();

    const [connection, setConnection] = useState<HubConnection>();
    const [connectionStatus, setConnectionStatus] = useState<HubConnectionState>(HubConnectionState.Disconnected);

    useEffect(() => {
        if (!partnershipId) {
            return;
        }

        const connection = new HubConnectionBuilder()
            .withUrl(`${MESSAGES_API}/messagehub`, { accessTokenFactory: getToken })
            .build();

        setConnection(connection);

        return () => {
            connection.stop();
        };
    }, [partnershipId, getToken, setConnectionStatus]);

    useEffect(() => {
        if (connection && connection.state === HubConnectionState.Disconnected) {
            connection.onclose(() => setConnectionStatus(connection.state));
            connection.onreconnected(() => setConnectionStatus(connection.state));
            connection.onreconnecting(() => setConnectionStatus(connection.state));

            connection
                .start()
                .then(() => setConnectionStatus(connection.state))
                .catch((err) => {
                    setConnectionStatus(connection.state);
                    error(err);
                });
        }
    }, [connection, setConnectionStatus, error]);

    const status = connection?.state;

    useEffect(() => {
        if (status) setConnectionStatus(status);
    }, [setConnectionStatus, status]);

    return <SignalContext.Provider value={{ connection, connectionStatus }}>{children}</SignalContext.Provider>;
};
